import classNames from 'classnames';
import parse from 'html-react-parser';
import { useRef, useState, ReactElement, useEffect, useCallback } from 'react';

import CardBeforeAfterEhealth, {
    CardBeforeAfterEhealthProps,
} from '~components/Card/CardBeforeAfterEhealth';
import CardBlogPost, { CardBlogPostProps } from '~components/Card/CardBlogPost';
import CardGeneric from '~components/Card/CardGeneric';
import CardWithIconAndBanner, {
    CardWithIconAndBannerProps,
} from '~components/Card/CardWithIconAndBanner';
import CardWithIconAndHiddenDescription, {
    CardWithIconAndHiddenDescriptionProps,
} from '~components/Card/CardWithIconAndHiddenDescription';
import ReviewCard from '~components/Card/ReviewCard';
import { getUrlParam } from '~globals/common';
import { fireTrackEvent } from '~globals/tracking';
import useWindowSize from '~hooks/useWindowSize';
import ArrowIcon from '~images/inline/arrow-rebrand.inline.svg';
import ChevronIcon from '~images/inline/chevron.inline.svg';
import CircleEmptyIcon from '~images/inline/circle-empty.inline.svg';
import CircleFilledIcon from '~images/inline/circle-filled.inline.svg';

import { largeBtn } from './CardCarousel.module.css';

type BottomNavOpt = 'none' | 'dots' | 'arrows';

interface CardCarouselProps {
    classes?: string;
    backgroundsurface: SupportedBackgroundColor;
    numcardsvisible: string;
    btmnavtypedesk: BottomNavOpt;
    btmnavtypemob: BottomNavOpt;
    btmnavtypetab: BottomNavOpt;
    children: ReactElement<
        | CardWithIconAndBannerProps
        | CardWithIconAndHiddenDescriptionProps
        | CardBlogPostProps
        | CardBeforeAfterEhealthProps
    >[];
    enablesidenavbtns: boolean;
    enablesidenavbtnsmobile: boolean;
    enablesidenavbtnstablet: boolean;
    heading: string;
    headingmobile?: string;
    subheading?: string;
    textcolor: SupportedTextColor;
}

export type DescriptionState = Record<number, boolean>;

type ModulesProp = {
    child: ReactElement<
        | CardWithIconAndBannerProps
        | CardWithIconAndHiddenDescriptionProps
        | CardBlogPostProps
        | CardBeforeAfterEhealthProps
    >;
    index: number;
};

const CardCarousel = ({
    classes,
    backgroundsurface,
    numcardsvisible,
    btmnavtypedesk,
    btmnavtypemob,
    btmnavtypetab,
    children,
    enablesidenavbtns,
    enablesidenavbtnsmobile,
    enablesidenavbtnstablet,
    heading,
    subheading,
    textcolor,
}: React.PropsWithChildren<CardCarouselProps>) => {
    const headingTag = heading.replace(/\s/g, '-').toLowerCase();
    const [currentPage, setCurrentPage] = useState(0);
    const [numCardsVisible, setNumCardsVisible] = useState(3);
    const [isInitialized, setIsInitialized] = useState(false);
    const isProgrammaticScroll = useRef(false); // Track programmatic scroll

    const { isMobile, isTablet, isDesktop, width } = useWindowSize();

    const cardScrollContainer = useRef<null | HTMLDivElement>(null);
    const observerRef = useRef<IntersectionObserver | null>(null);

    const currentPageCallbackRef = useRef(0);
    currentPageCallbackRef.current = currentPage;

    const doesRenderWithCurrentFlag = (
        card: ReactElement<
            | CardWithIconAndBannerProps
            | CardWithIconAndHiddenDescriptionProps
            | CardBlogPostProps
            | CardBeforeAfterEhealthProps
        >
    ) => {
        const { urlflag } = card.props;
        if (!urlflag) {
            return true;
        }
        if (urlflag.includes(':hide')) {
            const actualFlag = urlflag.replace(':hide', '');
            return getUrlParam(actualFlag) !== 'true';
        }
        return getUrlParam(urlflag) === 'true';
    };

    const childModules = children.filter(
        (child) => typeof child !== 'string' && doesRenderWithCurrentFlag(child)
    );

    const initialDescriptionState: DescriptionState = childModules.reduce((acc, _, index) => {
        acc[index] = false;
        return acc;
    }, {} as DescriptionState);

    const [toggleDescription, setToggleDescription] =
        useState<DescriptionState>(initialDescriptionState);
    const [showingHiddenText, setShowingHiddenText] = useState(false);

    const handleToggleDescription = useCallback(
        (index: number) => {
            const newState = Object.keys(toggleDescription).reduce((acc, key) => {
                acc[Number(key)] = Number(key) === index;
                return acc;
            }, {} as DescriptionState);

            // toggle carousel as showing a description somewhere, until re-rendered
            setToggleDescription(newState);
            // toggle carousel as showing a description somewhere, until re-rendered
            setShowingHiddenText(true);
        },
        [toggleDescription]
    );

    const trackingComponentName = 'Sliding Page Carousel';

    const hasAnyNavArrows = [
        enablesidenavbtnsmobile,
        enablesidenavbtnstablet,
        enablesidenavbtns,
    ].some(Boolean);

    const hasAnyBtmNavArrows = [btmnavtypedesk, btmnavtypetab, btmnavtypemob].some(
        (prop) => prop === 'arrows'
    );

    const hasAnyBtmNavDots = [btmnavtypedesk, btmnavtypetab, btmnavtypemob].some(
        (prop) => prop === 'dots'
    );

    const isFirstPage = () => currentPage === 0;

    const isLastPage = () => {
        if (cardScrollContainer.current) {
            return currentPage === childModules.length - numCardsVisible;
        }
        return false;
    };

    const scrollToCard = (i: number) => {
        // reset hidden descriptions
        setToggleDescription(initialDescriptionState);
        setCurrentPage(i);
        if (cardScrollContainer.current) {
            const gapBetweenItemPx = 24;
            const cardWidth = cardScrollContainer.current.firstElementChild?.clientWidth || 0;
            const pageWidth = cardWidth + gapBetweenItemPx;
            const newPosition = i * pageWidth;

            isProgrammaticScroll.current = true;
            cardScrollContainer.current.scrollTo({ left: newPosition, behavior: 'smooth' });
        }
    };

    const scrollToCardClick = (direction: string, i: number) => {
        fireTrackEvent({
            page: { action: `click: ${trackingComponentName}: ${direction}: scroll to card: ${i}` },
        });
    };

    const handleUserScroll = (entries: IntersectionObserverEntry[]) => {
        if (isProgrammaticScroll.current) {
            isProgrammaticScroll.current = false;
            return;
        }
        if (isDesktop) {
            // this can operate similar to Tablet logic below, but for 3 visible cards instead of 2
            // implement later if needed.
            return;
        }
        if (isTablet) {
            const fullyVisibleCards = entries.filter((entry) => entry.intersectionRatio === 1);
            // the following check ignores trigger on initialization, when user is not actually scrolling
            if (fullyVisibleCards.length === 1) {
                const newlyVisibleCard = fullyVisibleCards[0];
                const i = Number((newlyVisibleCard.target as HTMLDivElement).dataset.index);
                const didScrollLeft = i <= currentPageCallbackRef.current;

                // alternate logic may be needed if "dots" nav style ever implemented on Tablet...
                if (didScrollLeft) {
                    setCurrentPage(i);
                } else {
                    // select the leftmost of 2 fully visible cards
                    setCurrentPage(i - 1);
                }
            }
        }
        if (isMobile) {
            entries.forEach((entry) => {
                if (entry.intersectionRatio > 0.5) {
                    const newCurrentPage = Number((entry.target as HTMLDivElement).dataset.index);
                    setCurrentPage(newCurrentPage);
                }
            });
        }
    };

    const navArrowsDisplay = (): string => {
        const values = [
            {
                type: enablesidenavbtnsmobile,
                active: 'flex',
                hidden: 'hidden',
            },
            {
                type: enablesidenavbtnstablet,
                active: 'md:flex',
                hidden: 'md:hidden',
            },
            {
                type: enablesidenavbtns,
                active: 'lg:flex',
                hidden: 'lg:hidden',
            },
        ];
        const activeClasse = values.map((value) => (value.type ? value.active : value.hidden));

        return activeClasse.join(' ');
    };

    const bttmNavDisplay = (identifier: string): string => {
        const values = [
            {
                type: btmnavtypemob,
                active: 'flex',
                hidden: 'hidden',
            },
            {
                type: btmnavtypetab,
                active: 'md:flex',
                hidden: 'md:hidden',
            },
            {
                type: btmnavtypedesk,
                active: 'lg:flex',
                hidden: 'lg:hidden',
            },
        ];
        const activeClasse = values.map((value) =>
            value.type === identifier ? value.active : value.hidden
        );

        return activeClasse.join(' ');
    };

    useEffect(() => {
        observerRef.current = new IntersectionObserver(handleUserScroll, {
            root: cardScrollContainer.current,
            threshold: [0, 0.5, 1],
        });

        if (cardScrollContainer.current?.firstElementChild) {
            Array.from(cardScrollContainer.current.children).forEach((child) => {
                observerRef.current?.observe(child);
            });
        }

        setIsInitialized(true);

        return () => {
            observerRef.current?.disconnect();
        };
    }, []);

    useEffect(() => {
        if (isMobile && cardScrollContainer.current?.firstElementChild) {
            setNumCardsVisible(
                Math.floor(
                    cardScrollContainer.current.clientWidth /
                        cardScrollContainer.current.firstElementChild.clientWidth
                )
            );
        } else if (numcardsvisible) {
            const cardsNum = parseInt(numcardsvisible);

            setNumCardsVisible(cardsNum);
        } else {
            setNumCardsVisible(1);
        }
    }, [cardScrollContainer.current, isMobile, numcardsvisible]);

    useEffect(() => {
        if (isInitialized) {
            setCurrentPage(0);
            scrollToCard(0);
        }
    }, [width]);

    const Modules = ({ child, index }: ModulesProp) => {
        switch (true) {
            case child.type?.displayName === 'CardWithMedalion' && classes === 'testimonials': {
                return (
                    <ReviewCard
                        {...child.props}
                        key={index}
                        index={index}
                        ref={(el) => {
                            el && observerRef.current?.observe(el);
                        }}
                    />
                );
            }
            case child.type?.displayName === 'CardWithIconAndBanner': {
                return (
                    <CardWithIconAndBanner
                        {...child.props}
                        key={index}
                        minwidths="w-72 md:w-[336px] lg:w-[281px]"
                        index={index}
                        ref={(el) => {
                            el && observerRef.current?.observe(el);
                        }}
                    />
                );
            }
            case child.type?.displayName === 'CardWithIconAndHiddenDescription': {
                return (
                    <CardWithIconAndHiddenDescription
                        {...child.props}
                        key={index}
                        minwidths="w-72 md:w-[246px] lg:w-[316px]"
                        maxheights="h-[428px] lg:h-[360px]"
                        index={index}
                        showingHiddenText={showingHiddenText}
                        toggleDescription={toggleDescription[index]}
                        setToggleDescription={handleToggleDescription}
                        ref={(el) => {
                            el && observerRef.current?.observe(el);
                        }}
                    />
                );
            }
            case child.type?.displayName === 'CardBlogPost': {
                return (
                    <CardBlogPost
                        {...child.props}
                        key={index}
                        minwidths="min-w-[281px] md:min-w-[246px] lg:min-w-[316px]"
                        index={index}
                        ref={(el) => {
                            el && observerRef.current?.observe(el);
                        }}
                    />
                );
            }
            case child.type?.displayName === 'CardBeforeAfterEhealth': {
                return (
                    <CardBeforeAfterEhealth
                        {...child.props}
                        key={index}
                        minwidths="min-w-72 md:min-w-[696px]"
                        index={index}
                        ref={(el) => {
                            el && observerRef.current?.observe(el);
                        }}
                    />
                );
            }
            default: {
                return (
                    <CardGeneric
                        {...child.props}
                        key={index}
                        index={index}
                        ref={(el) => {
                            el && observerRef.current?.observe(el);
                        }}
                    />
                );
            }
        }
    };

    return (
        <section
            className={classNames(classes, backgroundsurface, 'pt-6 md:pt-10 lg:pt-20 pb-6 px-4')}
        >
            <div className="max-w-[1200px] mx-auto flex justify-center flex-col">
                <div className="flex flex-col items-center justify-center mb-6 lg:mb-12">
                    <h2
                        className={classNames(
                            'font-bold font-poppins',
                            'text-24 leading-32 md:text-32 md:leading-40 lg:leading-56',
                            'mx-auto text-center w-full',
                            textcolor
                        )}
                    >
                        {parse(heading)}
                    </h2>
                    {subheading && (
                        <h3 className="hidden lg:block font-bold font-poppins text-18 leading-28 text-primary-dark">
                            {subheading}
                        </h3>
                    )}
                </div>
                {childModules.length > 0 && (
                    <div className="card-wrap flex gap-6 h-auto overflow-y-hidden w-full">
                        {hasAnyNavArrows && (
                            <button
                                type="button"
                                onClick={() => {
                                    scrollToCard(currentPage - 1);
                                    scrollToCardClick('left', currentPage - 1);
                                }}
                                disabled={isFirstPage()}
                                aria-label="previous"
                                className={classNames(
                                    navArrowsDisplay(),
                                    'justify-center items-center',
                                    'bg-transparent min-w-[66px] rounded-bl-full',
                                    largeBtn as string
                                )}
                                data-qa={`previous:side:${headingTag}`}
                            >
                                <div
                                    className={classNames(
                                        'border-2 border-transparent',
                                        'w-full h-20 flex items-center justify-center'
                                    )}
                                >
                                    <ChevronIcon
                                        className={classNames(
                                            'rotate-90 h-12 w-12 text-primary-dark',
                                            isFirstPage() && 'opacity-40'
                                        )}
                                    />
                                </div>
                            </button>
                        )}
                        <div
                            className="card-grid flex gap-6 p-1 overflow-y-hidden overflow-x-scroll lg:overflow-x-hidden scrollbar-hidden mx-auto w-full"
                            ref={cardScrollContainer}
                        >
                            {isInitialized &&
                                childModules.map((child, i) => (
                                    <article key={i} className="flex">
                                        {<Modules child={child} index={i} />}
                                    </article>
                                ))}
                        </div>
                        {hasAnyNavArrows && (
                            <button
                                type="button"
                                onClick={() => {
                                    scrollToCard(currentPage + 1);
                                    scrollToCardClick('right', currentPage + 1);
                                }}
                                disabled={isLastPage()}
                                aria-label="next"
                                className={classNames(
                                    navArrowsDisplay(),
                                    'justify-center items-center',
                                    'bg-transparent min-w-[66px] rounded-r-full',
                                    largeBtn as string
                                )}
                                data-qa={`next:side:${headingTag}`}
                            >
                                <div
                                    className={classNames(
                                        'border-2 border-transparent',
                                        'w-full h-20 flex items-center justify-center'
                                    )}
                                >
                                    <ChevronIcon
                                        className={classNames(
                                            '-rotate-90 h-12 w-12 text-primary-dark',
                                            isLastPage() && 'opacity-40'
                                        )}
                                    />
                                </div>
                            </button>
                        )}
                    </div>
                )}

                {/* Bottom Bav Type: Dots: page selector/indicator buttons */}
                {hasAnyBtmNavDots && (
                    // mobile/tablet dots NOT yet implemented. build later if needed
                    <div
                        className={classNames('justify-center w-auto gap-6 m-auto mt-9', bttmNavDisplay('dots'))}
                    >
                        {childModules.map((x, i) => {
                            const isShownOnFirstPage = i + 1 < numCardsVisible;
                            const greatestIndexFullyVisible = currentPage + (numCardsVisible - 1);
                            const target = i + 1 - numCardsVisible;
                            const ariaLabel = 'step ' + i.toString();

                            if (isShownOnFirstPage) {
                                return;
                            } else if (i === greatestIndexFullyVisible) {
                                return (
                                    <button
                                        key={i}
                                        type="button"
                                        aria-label={ariaLabel}
                                        onClick={() => null}
                                    >
                                        <CircleFilledIcon className="text-link-visited" />
                                    </button>
                                );
                            }

                            return (
                                <button
                                    key={i}
                                    type="button"
                                    aria-label={ariaLabel}
                                    onClick={() => {
                                        scrollToCard(target);
                                        scrollToCardClick('bottom', target);
                                    }}
                                >
                                    <CircleEmptyIcon className="text-icon-primary" />
                                </button>
                            );
                        })}
                    </div>
                )}

                {/* Bottom Nav Type: Arrows: arrow buttons to next/previous page */}
                {hasAnyBtmNavArrows && (
                    <div
                        className={classNames(
                            'flex justify-center gap-6 mt-0 md:mt-3',
                            bttmNavDisplay('arrows')
                        )}
                    >
                        <button
                            type="button"
                            onClick={() => {
                                scrollToCard(currentPage - 1);
                                scrollToCardClick('left', currentPage - 1);
                            }}
                            disabled={isFirstPage()}
                            aria-label="previous"
                            data-qa={`previous:bottom:${headingTag}`}
                        >
                            <ArrowIcon
                                className={classNames(
                                    'rotate-180 mx-4',
                                    'text-icon-primary',
                                    isFirstPage() && 'opacity-40'
                                )}
                            />
                        </button>
                        <button
                            type="button"
                            onClick={() => {
                                scrollToCard(currentPage + 1);
                                scrollToCardClick('right', currentPage + 1);
                            }}
                            disabled={isLastPage()}
                            aria-label="next"
                            data-qa={`next:bottom:${headingTag}`}
                        >
                            <ArrowIcon
                                className={classNames(
                                    'mx-4',
                                    'text-icon-primary',
                                    isLastPage() && 'opacity-40'
                                )}
                            />
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
};

export default CardCarousel;
